import { useCallback, useEffect, useState } from "react";
import ApiService from "../../core/service/ApiService";
import { useUser } from "../../core/context/UserContext";

// Tanımlı izin id'leri tüm ekranlarda aynı; tek istekle alınıp saklanır.
let definedIdsPromise = null;

const loadDefinedIds = () => {
    if (!definedIdsPromise) {
        definedIdsPromise = ApiService.Role.GetPermissionGroups()
            .then(res => res?.success
                ? new Set(res.data.flatMap(g => g.permissions.map(p => p.id)))
                : null)
            .catch(() => null)
            .then(ids => {
                // Başarısızsa sonraki çağrı yeniden denesin
                if (!ids) definedIdsPromise = null;
                return ids;
            });
    }
    return definedIdsPromise;
};

/**
 * Rol/kullanıcı yönetiminde yetki yükseltme koruması — backend PermissionHelper.GetNotGrantablePermissionIds
 * ile aynı kural: kimse kendinde olmayan bir izni veremez; süper admin muaf. Rollerde kalıntı olarak
 * duran, artık tanımlı olmayan eski id'ler dikkate alınmaz.
 * Arayüz yalnız yol gösterir (seçenekleri pasifleştirir); asıl engel sunucuda.
 */
export default function usePermissionGuard() {
    const { hasPermission, isSuperAdmin } = useUser();
    const [definedIds, setDefinedIds] = useState(null);

    useEffect(() => {
        let alive = true;
        loadDefinedIds().then(ids => alive && setDefinedIds(ids));
        return () => { alive = false };
    }, []);

    const notGrantable = useCallback((ids = []) => {
        if (isSuperAdmin() || !definedIds)
            return [];

        return [...new Set(ids ?? [])].filter(id => definedIds.has(id) && !hasPermission(id));
    }, [definedIds, hasPermission, isSuperAdmin]);

    const canGrant = useCallback((ids = []) => notGrantable(ids).length === 0, [notGrantable]);

    /** Kullanıcının rolünden + ek izinlerinden gelen tüm izinler verilebilir mi (yani o kullanıcı yönetilebilir mi). */
    const canManageUser = useCallback((user) => canGrant([
        ...(user?.role?.permissionIdList ?? []),
        ...(user?.permissionIdList ?? [])
    ]), [canGrant]);

    return {
        ready: !!definedIds,
        isSuperAdmin: isSuperAdmin(),
        canGrantPermission: (id) => isSuperAdmin() || hasPermission(id),
        notGrantable,
        canGrant,
        canManageUser
    };
}
